import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserCircle2, Loader2, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";

const YEAR_LEVELS = ["1st Year", "2nd Year", "3rd Year", "4th Year", "5th Year", "Graduate", "Reviewee"];

const FIELD_LABELS = {
  full_name: "Full Name",
  email: "Email",
  contact_number: "Contact Number",
  selected_course_id: "Selected Course",
};

export default function StudentProfileComplete() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/student/enrollments";
  const { setUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    contact_number: "",
    selected_course_id: "",
    year_level: "",
    board_exam_category: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [prof, status, cs] = await Promise.all([
          api.get("/student/profile"),
          api.get("/student/profile/completion-status"),
          api.get("/courses"),
        ]);
        setMissing(status.data.missing_fields || []);
        setCourses(cs.data || []);
        setForm((f) => ({
          ...f,
          full_name: prof.data.full_name || "",
          email: prof.data.email || "",
          contact_number: prof.data.contact_number || "",
          selected_course_id: prof.data.selected_course_id || "",
          year_level: prof.data.year_level || "",
          board_exam_category: prof.data.board_exam_category || "",
        }));
      } catch (e) {
        toast.error("Could not load profile");
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch("/student/profile", {
        full_name: form.full_name.trim(),
        contact_number: form.contact_number.trim(),
        selected_course_id: form.selected_course_id,
        year_level: form.year_level,
        board_exam_category: form.board_exam_category.trim(),
      });
      const completion = r.data?.profile_completion || { is_complete: true };
      if (r.data?.user) {
        localStorage.setItem("pm_user", JSON.stringify(r.data.user));
        setUser(r.data.user);
      }
      if (completion.is_complete) {
        toast.success("Profile complete! You may now proceed.");
        nav(next, { replace: true });
      } else {
        setMissing(completion.missing_fields || []);
        toast.warning("Some required fields are still missing");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-complete-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 flex items-center gap-2">
          <UserCircle2 className="w-3.5 h-3.5" /> Complete Profile
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 text-slate-900 dark:text-slate-50">
          Complete Your Profile
        </h1>
        <p className="text-slate-600 dark:text-slate-300 mt-1 max-w-2xl">
          These details are optional and help us personalize your experience.
          You can update them any time from your account.
        </p>
      </div>

      {missing.length > 0 && (
        <Card className="p-4 border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30" data-testid="profile-missing-fields-card">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 dark:text-amber-300 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-900 dark:text-amber-100">Missing information:</div>
              <ul className="mt-1 text-sm text-amber-900 dark:text-amber-100 list-disc list-inside">
                {missing.map((f) => (
                  <li key={f}>{FIELD_LABELS[f] || f}</li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      <form onSubmit={save}>
        <Card className="p-6 border-slate-200 dark:border-slate-700 space-y-5">
          <SectionTitle title="Your details" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Full Name <span className="text-slate-400 text-xs ml-1">optional</span></Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="profile-fullname-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} disabled readOnly className="bg-slate-50 dark:bg-slate-800/60 cursor-not-allowed" data-testid="profile-email-input" />
              <p className="text-[11px] text-slate-500 mt-1">Email cannot be changed here.</p>
            </div>
            <div>
              <Label>Contact Number <span className="text-slate-400 text-xs ml-1">optional</span></Label>
              <Input
                value={form.contact_number}
                onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
                placeholder="+63 9XX XXX XXXX"
                data-testid="profile-contact-input"
              />
            </div>
            <div>
              <Label>Year Level <span className="text-slate-400 text-xs ml-1">optional</span></Label>
              <Select value={form.year_level} onValueChange={(v) => setForm({ ...form, year_level: v })}>
                <SelectTrigger data-testid="profile-year-select"><SelectValue placeholder="Select year level" /></SelectTrigger>
                <SelectContent>
                  {YEAR_LEVELS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Board Exam Category <span className="text-slate-400 text-xs ml-1">optional</span></Label>
              <Input value={form.board_exam_category} onChange={(e) => setForm({ ...form, board_exam_category: e.target.value })} placeholder="e.g. LET, NLE, CPALE…" data-testid="profile-category-input" />
            </div>
          </div>
        </Card>

        <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
          <Button type="button" variant="outline" onClick={() => nav(next)} data-testid="profile-skip-btn">Skip for now</Button>
          <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="profile-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Save and Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </form>
    </div>
  );
}

function SectionTitle({ title }) {
  return (
    <div className="flex items-center gap-2">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{title}</h3>
      <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}
